/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { mapModifiers } from 'libs/component';
import Default from 'components/templates/default';
import { Heading } from 'components/atoms/heading';
import { Text } from 'components/atoms/text';
import { Section } from 'components/molecules/section';
import { OptionList } from 'components/molecules/option-list';
// import { InfoCardHighlight } from 'components/atoms/info-card-highlight';
import { DistanceText } from 'components/atoms/distance-text';
// import { LocationInfoItem } from 'components/atoms/location-info-item';
// import { Carousel } from 'components/organisms/carousel';
// import { InfoCardItem } from 'components/atoms/info-card-item';
import { HOTEL_SURROUNDING_OPTION_LIST, HOTEL_LIST, SURROUNDING_LIST } from 'libs/constants';
import { PoiCard } from 'components/atoms/poi-card';
import { PoiCardList } from 'components/molecules/poi-card-list';
import { Icon } from 'components/atoms/icon';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const heroImage = '/images/hero-banners/hotel-surrounding.jpg';
const heroImageSp = '/images/hero-banners/hotel-surrounding-sp.jpg';

export interface HotelSurroundingProps {
  currentTab?: number;
  onOptionSelected?: (tab: number) => void;
  onItemClick?: (type: string, content: string) => void;
}

export const HotelSurrounding: React.FC<HotelSurroundingProps> = ({
  onItemClick,
  onOptionSelected,
  currentTab: defaultCurrentTab = 0,
}) => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isStartLoading, setIsStartLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentTab, setCurrentTab] = useState(defaultCurrentTab);

  const optionList = useMemo(() => {
    return HOTEL_SURROUNDING_OPTION_LIST;
  }, []);

  const hotelList = useMemo(() => {
    return HOTEL_LIST;
  }, []);

  const surroundingList = useMemo(() => {
    return SURROUNDING_LIST;
  }, []);

  const optionItemList = optionList.map(x => x.label);

  const handleOptionSelected = useCallback(
    (index: number) => {
      setCurrentTab(index);
      if (onOptionSelected) {
        onOptionSelected(index);
      }
    },
    [setCurrentTab, onOptionSelected]
  );

  // const hotelHighlight = hotelList[0];
  // const hotelItems = hotelList.filter((_, i) => i > 0);
  // const surroundingHighlight = surroundingList[0];
  // const surroundingItems = surroundingList.filter((_, i) => i > 0);

  const handleItemClick = useCallback(
    (value: string) => {
      if (onItemClick) {
        const type = currentTab === 0 ? 'hotel' : 'surrounding';
        onItemClick(type, value);
      }
    },
    [currentTab, onItemClick]
  );

  const initPage = useCallback(() => {
    if (!isStartLoading) {
      setIsStartLoading(true);
    } else {
      return;
    }
    // const isMobile = window.innerWidth <= 640;
    let imageLoadedCount = 0;
    const imageList = [heroImage, heroImageSp];

    hotelList.forEach((item: any) => {
      imageList.push(item.image);
      imageList.push(item.imageSp);
    });

    surroundingList.forEach((item: any) => {
      imageList.push(item.image);
      imageList.push(item.imageSp);
    });

    const imageLoaded = () => {
      imageLoadedCount += 1;
      const progress = Math.ceil((imageLoadedCount / imageList.length) * 100);
      setLoadingProgress(progress);
      // console.log('imageLoaded', imageLoadedCount, '/', imageList.length, e.target?.src);
      if (imageLoadedCount >= imageList.length) {
        setIsLoading(false);
      }
    };
    imageList.forEach(url => {
      const img = new Image();
      img.src = url;
      img.onload = imageLoaded;
    });

    // wait for 1 minutes then force stop loading in case there are some errors or take too long to load
    setTimeout(() => {
      setIsLoading(false);
    }, 60 * 1000);
  }, [hotelList, isStartLoading, surroundingList]);

  useEffect(() => {
    if (isLoading) {
      initPage();
    }
  }, [initPage, isLoading]);

  return (
    <div className={mapModifiers('t-hotel-surrounding', i18n.language)}>
      <Default
        heroImage={heroImage}
        heroImageSp={heroImageSp}
        isLoading={isLoading}
        isStartLoading={isStartLoading}
        loadingProgress={loadingProgress}
      >
        <Heading>酒店与周边环境</Heading>

        <Section className="t-hotel-surrounding__tabs">
          <OptionList
            currentIndex={currentTab}
            modifiers="horizontal"
            items={optionItemList}
            onSelected={handleOptionSelected}
          />
        </Section>

        <AnimatePresence exitBeforeEnter>
          {currentTab === 0 && (
            <motion.div
              key="tab-0"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: currentTab === 0 ? 1 : 0,
              }}
              exit={{
                opacity: 0,
              }}
              transition={{
                duration: 0.5,
                type: 'spring',
              }}
            >
              <div
                className={mapModifiers('t-hotel-surrounding__description',
                  currentTab === 0 ? 'hotel' : 'surrounding',
                  i18n.language,
                )}
              >
                <Text>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('hotel-surrounding.hotel.description'),
                    }}
                  />
                </Text>
              </div>
              <Section className={mapModifiers('t-hotel-surrounding__info', 'hotel')}>
                {/*
              <div className="t-hotel-surrounding__info-hotel">
                <InfoCardHighlight
                  modifiers="surrounding"
                  image={hotelHighlight.image}
                  imageSp={hotelHighlight.imageSp}
                  title={hotelHighlight.title}
                  distance={
                    <DistanceText distance={hotelHighlight.distance.value} unit={hotelHighlight.distance.unit}>
                      Distance
                    </DistanceText>
                  }
                  description={hotelHighlight.description}
                  value={hotelHighlight.slug}
                  onClick={handleItemClick}
                >
                  {hotelHighlight.locations.map((loc, index) => (
                    <LocationInfoItem key={index} type={loc.type as any}>
                      {!loc.value && <span>{loc.text}</span>}
                      {loc.value && (
                        <a href={loc.value} target="_blank" rel="noreferrer">
                          {loc.text}
                        </a>
                      )}
                    </LocationInfoItem>
                  ))}
                </InfoCardHighlight>
              </div>

              <div className="t-hotel-surrounding__info-hotel-carousel">
                <Carousel mode="surrounding">
                  {hotelItems.map((hotel, index) => (
                    <InfoCardItem
                      key={index}
                      image={hotel.image}
                      imageSp={hotel.imageSp}
                      title={hotel.title}
                      distance={
                        <DistanceText distance={hotel.distance.value} unit={hotel.distance.unit}>
                          Distance
                        </DistanceText>
                      }
                      value={hotel.slug}
                      onClick={handleItemClick}
                    >
                      {hotel.description}
                    </InfoCardItem>
                  ))}
                </Carousel>
              </div>
            */}
                <PoiCardList>
                  {hotelList.map((hotel: any, index: number) => (
                    <PoiCard
                      key={index}
                      image={hotel.image}
                      imageSp={hotel.imageSp}
                      imageWebp={hotel.imageWebp}
                      imageWebpSp={hotel.imageWebpSp}
                      title={(hotel.title as any)[i18n.language]}
                      summary={(hotel.description as any)[i18n.language]}
                      distance={
                        <DistanceText distance={hotel.distance.value} unit={t(`unit.${hotel.distance.unit}`)}>
                          <Icon name="marker" color="grey" />
                        </DistanceText>
                      }
                      value={hotel.slug}
                      onClick={handleItemClick}
                    />
                  ))}
                </PoiCardList>
              </Section>
            </motion.div>
          )}

          {currentTab === 1 && (
            <motion.div
              key="tab-1"
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: currentTab === 1 ? 1 : 0,
              }}
              exit={{
                opacity: 0,
              }}
              transition={{
                duration: 0.5,
                type: 'spring',
              }}
            >
              <div className="t-hotel-surrounding__description">
                <Text>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('hotel-surrounding.surrounding.description'),
                    }}
                  />
                </Text>
              </div>
              <Section className={mapModifiers('t-hotel-surrounding__info', 'surrounding')}>
                {/*
              <div className="t-hotel-surrounding__info-surrounding">
                <InfoCardHighlight
                  modifiers="surrounding"
                  image={surroundingHighlight.image}
                  imageSp={surroundingHighlight.imageSp}
                  title={surroundingHighlight.title}
                  distance={
                    <DistanceText
                      distance={surroundingHighlight.distance.value}
                      unit={surroundingHighlight.distance.unit}
                    >
                      Distance
                    </DistanceText>
                  }
                  description={surroundingHighlight.description}
                  value={surroundingHighlight.slug}
                  onClick={handleItemClick}
                >
                  {surroundingHighlight.locations.map((loc, index) => (
                    <LocationInfoItem key={index} type={loc.type as any}>
                      {!loc.value && <span>{loc.text}</span>}
                      {loc.value && (
                        <a href={loc.value} target="_blank" rel="noreferrer">
                          {loc.text}
                        </a>
                      )}
                    </LocationInfoItem>
                  ))}
                </InfoCardHighlight>
              </div>

              <div className="t-hotel-surrounding__info-surrounding-carousel">
                <Carousel mode="surrounding">
                  {surroundingItems.map((surrounding, index) => (
                    <InfoCardItem
                      key={index}
                      image={surrounding.image}
                      imageSp={surrounding.imageSp}
                      title={surrounding.title}
                      distance={
                        <DistanceText distance={surrounding.distance.value} unit={surrounding.distance.unit}>
                          Distance
                        </DistanceText>
                      }
                      value={surrounding.slug}
                      onClick={handleItemClick}
                    >
                      {surrounding.description}
                    </InfoCardItem>
                  ))}
                </Carousel>
              </div>
              */}
                <PoiCardList>
                  {surroundingList.map((surrounding: any, index: number) => (
                    <PoiCard
                      key={index}
                      image={surrounding.image}
                      imageSp={surrounding.imageSp}
                      imageWebp={surrounding.imageWebp}
                      imageWebpSp={surrounding.imageWebpSp}
                      title={(surrounding.title as any)[i18n.language]}
                      distance={
                        <DistanceText
                          distance={surrounding.distance.value}
                          unit={t(`unit.${surrounding.distance.unit}`)}
                        >
                          <Icon name="marker" color="grey" />
                        </DistanceText>
                      }
                      value={surrounding.slug}
                      onClick={handleItemClick}
                    />
                  ))}
                </PoiCardList>
              </Section>
            </motion.div>
          )}
        </AnimatePresence>
      </Default>
    </div>
  );
};
